import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";
import Decimal from "decimal.js";

import {
    ROUTE_GET_LENDING_VAULT_ALLOCATIONS,
    ROUTE_GET_LENDING_VAULTS,
    ROUTE_GET_LENDING_VAULTS_HISTORY,
    ROUTE_GET_LENDING_VAULTS_UPDATE_HISTORY
} from "../constants";
import { abfSerializeQueryArgs, marketsPublicBaseQuery } from "./util";
import {
    CapParameters,
    FetchLendVaultsHistoryParams,
    FetchLendVaultsInfoParams,
    LendingVaultAllocation,
    LendingVaultsHistory,
    LendingVaultsInfoResponse,
    ParsedCollateralTerms,
    ParsedTermsUpdate,
    ParsedTimeLock,
    ParsedUpdateAssetDataParams,
    ParsedUpdateStrategySettingsParams,
    ParsedUpdateVaultSettingsParams,
    RawLendingVaultsInfoResponse,
    RawParsedAddCollateralTimelockArgs,
    RawParsedTermsUpdate,
    RawParsedUpdateAssetDataParams,
    VaultUpdateActionType
} from "../types";

const LENDING_PUBLIC_VAULT_TAG = "LendingVault";

export const lendingVaultsPublicApi = createApi({
    reducerPath: "abfLendingVaultPublicApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [LENDING_PUBLIC_VAULT_TAG],
    endpoints: (builder) => ({
        lendingVaultInfos: builder.query<LendingVaultsInfoResponse, FetchLendVaultsInfoParams>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_VAULTS,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (response: RawLendingVaultsInfoResponse): LendingVaultsInfoResponse => {
                return {
                    lendVaults: response.lendVaults.map((vault) => ({
                        ...vault,
                        pendingTerms: vault.pendingTerms.map((timelock): ParsedTimeLock => {
                            const rawUpdate = timelock.update;
                            const parsedUpdate = {} as ParsedTermsUpdate;
                            for (const key of Object.keys(rawUpdate)) {
                                const action = key as keyof RawParsedTermsUpdate;
                                const value = rawUpdate[action];

                                switch (action) {
                                    case VaultUpdateActionType.UpdateCollateralAllocationPct:
                                    case VaultUpdateActionType.UpdateLtv:
                                        if (value == null) {
                                            parsedUpdate[action] = null;
                                        } else {
                                            parsedUpdate[action] = convertRawUpdateAssetDataParams(
                                                value as RawParsedUpdateAssetDataParams
                                            );
                                        }
                                        break;

                                    case VaultUpdateActionType.AddCollateral:
                                        if (value) {
                                            const addCollateral = value as RawParsedAddCollateralTimelockArgs;
                                            parsedUpdate[action] = {
                                                ...addCollateral,
                                                updateAssetDataParams: convertRawUpdateAssetDataParams(
                                                    addCollateral.updateAssetDataParams
                                                )
                                            };
                                        } else {
                                            parsedUpdate[action] = null;
                                        }
                                        break;

                                    case VaultUpdateActionType.UpdateStrategyMarketInformation:
                                        parsedUpdate[action] = value as string | null;
                                        break;
                                    case VaultUpdateActionType.RemoveCollateral:
                                    case VaultUpdateActionType.UpdateApy:
                                        parsedUpdate[action] = value as ParsedCollateralTerms | null;
                                        break;
                                    case VaultUpdateActionType.UpdateStrategySettings:
                                        parsedUpdate[action] = value as ParsedUpdateStrategySettingsParams | null;
                                        break;
                                    case VaultUpdateActionType.UpdateVaultSettings:
                                        parsedUpdate[action] = value as ParsedUpdateVaultSettingsParams | null;
                                        break;
                                    case VaultUpdateActionType.CapsUpdate:
                                        parsedUpdate[action] = value as CapParameters | null;
                                        break;
                                    default:
                                        // Should not reach here, but fallback
                                        parsedUpdate[action] = value as never;
                                        break;
                                }
                            }

                            return {
                                ...timelock,
                                update: parsedUpdate
                            };
                        })
                    })),
                    total: response.total
                };
            }
        }),
        lendingVaultHistory: builder.query<LendingVaultsHistory[], FetchLendVaultsHistoryParams>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_VAULTS_HISTORY,
                    method: "POST",
                    body: params
                };
            }
        }),
        lendingVaultUpdateHistory: builder.query<string, void>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query() {
                return {
                    url: ROUTE_GET_LENDING_VAULTS_UPDATE_HISTORY,
                    method: "POST"
                };
            }
        }),
        lendingVaultAllocations: builder.query<LendingVaultAllocation[], { vaultAddress: string }>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: `${ROUTE_GET_LENDING_VAULT_ALLOCATIONS}/${params.vaultAddress}`,
                    method: "GET"
                };
            }
        })
    })
});

export const {
    useLendingVaultInfosQuery,
    useLendingVaultHistoryQuery,
    useLendingVaultUpdateHistoryQuery,
    useLendingVaultAllocationsQuery
} = lendingVaultsPublicApi;

export function useLendingVaultsPublicApi() {
    const dispatch = useDispatch();

    return {
        resetLendingVaultPublicApi: () =>
            dispatch(lendingVaultsPublicApi.util.invalidateTags([LENDING_PUBLIC_VAULT_TAG]))
    };
}

function convertRawUpdateAssetDataParams(raw: RawParsedUpdateAssetDataParams): ParsedUpdateAssetDataParams {
    const { maxCollateralAllocationPct, ...rest } = raw;

    return {
        ...rest,
        maxCollateralAllocationPct: convertMaxAllocPct(maxCollateralAllocationPct)
    };
}

function convertMaxAllocPct(maxAllocPct: string | null | undefined): Decimal | null {
    if (!maxAllocPct) return null;
    return new Decimal(maxAllocPct);
}
